'use client';
import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { FollowUpReport, ObservationItem } from './followUpData';

interface Props {
  reports: FollowUpReport[];
  onClose: () => void;
  onApply: (reportId: string, updates: Partial<ObservationItem>[]) => void;
}

// Parse owner response Excel/CSV and extract observation updates
function parseOwnerResponseFile(rows: string[][]): Partial<ObservationItem>[] {
  const updates: Partial<ObservationItem>[] = [];
  if (rows.length < 2) return updates;

  const headers = rows[0].map((h) => String(h).toLowerCase().trim());
  const refIdx = headers.findIndex((h) => h.includes('ref') || h.includes('item'));
  const actionIdx = headers.findIndex((h) => h.includes('corrective') || h.includes('action'));
  const statusIdx = headers.findIndex((h) => h.includes('status'));
  const commentIdx = headers.findIndex((h) => h.includes('comment') || h.includes('remark'));
  const closedIdx = headers.findIndex((h) => h.includes('closed') || h.includes('close date'));

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every((c) => !c)) continue;

    const ref = refIdx >= 0 ? String(row[refIdx] || '').trim() : '';
    if (!ref) continue;

    const statusRaw = statusIdx >= 0 ? String(row[statusIdx] || '').trim().toLowerCase() : '';
    let status: ObservationItem['status'] | undefined;
    if (statusRaw.includes('close')) status = 'Closed';
    else if (statusRaw.includes('progress') || statusRaw.includes('wip')) status = 'In Progress';
    else if (statusRaw.includes('open')) status = 'Open';

    const update: Partial<ObservationItem> = { ref };
    if (actionIdx >= 0 && row[actionIdx]) update.correctiveAction = String(row[actionIdx]).trim();
    if (status) update.status = status;
    if (commentIdx >= 0 && row[commentIdx]) update.ownerComment = String(row[commentIdx]).trim();
    if (closedIdx >= 0 && row[closedIdx]) update.closedDate = String(row[closedIdx]).trim();

    updates.push(update);
  }
  return updates;
}

export default function OwnerResponseUpload({ reports, onClose, onApply }: Props) {
  const [selectedReportId, setSelectedReportId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsedUpdates, setParsedUpdates] = useState<Partial<ObservationItem>[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const handleFile = async (f: File) => {
    if (!f.name.match(/\.(xlsx|xls|xlsm|csv)$/i)) {
      toast.error('Please upload an Excel or CSV file');
      return;
    }
    setFile(f);
    setParsing(true);
    setParsedUpdates(null);
    setWarnings([]);

    try {
      const XLSX = await import('xlsx');
      const buffer = await f.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as string[][];
      const updates = parseOwnerResponseFile(rows);

      const warns: string[] = [];
      if (updates.length === 0) warns.push('No observation updates detected. Check that the file has Ref and Status columns.');

      // Check which refs match the selected report
      if (selectedReportId) {
        const report = reports.find((r) => r.id === selectedReportId);
        if (report) {
          const reportRefs = new Set(report.observations.map((o) => o.ref));
          const unmatchedRefs = updates.filter((u) => u.ref && !reportRefs.has(u.ref)).map((u) => u.ref);
          if (unmatchedRefs.length > 0) {
            warns.push(`${unmatchedRefs.length} ref(s) in the file don't match this report: ${unmatchedRefs.slice(0, 3).join(', ')}${unmatchedRefs.length > 3 ? '...' : ''}`);
          }
        }
      }

      setParsedUpdates(updates);
      setWarnings(warns);
    } catch (err) {
      console.error(err);
      toast.error('Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  const handleApply = () => {
    if (!selectedReportId || !parsedUpdates) return;
    onApply(selectedReportId, parsedUpdates);
  };

  const selectedReport = reports.find((r) => r.id === selectedReportId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <div>
            <h2 className="font-bold text-base">Upload Owner Response Report</h2>
            <p className="text-xs text-muted mt-0.5">Extract updates from completed owner response file</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-foreground transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
            <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              When you receive a completed owner response report (Excel/CSV), upload it here.
              The platform will extract corrective actions, status updates, and evidence references
              and automatically reflect them against the original inspection record.
            </p>
          </div>

          {/* Select Report */}
          <div>
            <label className="block text-xs font-semibold mb-1.5 text-muted uppercase tracking-wide">Select Inspection Report</label>
            <select
              className="input-field text-sm w-full"
              value={selectedReportId}
              onChange={(e) => setSelectedReportId(e.target.value)}
            >
              <option value="">— Select a vessel report —</option>
              {reports.map((r) => (
                <option key={`ropt-${r.id}`} value={r.id}>
                  {r.vesselName} (IMO {r.imoNumber}) — {r.openCount} open items
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-xs font-semibold mb-1.5 text-muted uppercase tracking-wide">Owner Response File</label>
            <div
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-blue-50 transition-all"
              style={{ borderColor: file ? 'hsl(var(--primary))' : undefined }}
              onClick={() => document.getElementById('owner-response-input')?.click()}
            >
              <input
                id="owner-response-input"
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet size={18} style={{ color: 'hsl(var(--primary))' }} />
                  <span className="text-sm font-medium">{file.name}</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={24} className="text-muted" />
                  <p className="text-sm text-muted">Drop owner response file here or click to browse</p>
                  <p className="text-xs text-muted">.xlsx, .xls, .csv supported</p>
                </div>
              )}
            </div>
          </div>

          {/* Parsing indicator */}
          {parsing && (
            <div className="flex items-center gap-2 text-sm text-muted">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              Parsing file...
            </div>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={14} className="text-amber-600" />
                <span className="text-xs font-semibold text-amber-800">Warnings</span>
              </div>
              {warnings.map((w, i) => (
                <p key={`w-${i}`} className="text-xs text-amber-700">• {w}</p>
              ))}
            </div>
          )}

          {/* Parsed Preview */}
          {parsedUpdates && parsedUpdates.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={14} className="text-green-600" />
                <span className="text-xs font-semibold text-green-700">{parsedUpdates.length} observation updates detected</span>
              </div>
              <div className="border rounded-xl overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
                      <th className="px-3 py-2 text-left font-semibold text-muted">Ref</th>
                      <th className="px-3 py-2 text-left font-semibold text-muted">Status</th>
                      <th className="px-3 py-2 text-left font-semibold text-muted">Corrective Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedUpdates.slice(0, 8).map((u, i) => (
                      <tr key={`pu-${i}`} className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
                        <td className="px-3 py-2 font-mono font-bold text-primary">{u.ref}</td>
                        <td className="px-3 py-2">
                          {u.status ? (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                              u.status === 'Closed' ? 'bg-green-100 text-green-700' :
                              u.status === 'In Progress'? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>{u.status}</span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2 max-w-xs truncate text-muted">{u.correctiveAction || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedUpdates.length > 8 && (
                  <p className="px-3 py-2 text-xs text-muted">+ {parsedUpdates.length - 8} more updates</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: 'hsl(var(--border))' }}>
          <button className="btn-secondary text-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary text-sm"
            onClick={handleApply}
            disabled={!selectedReportId || !parsedUpdates || parsedUpdates.length === 0}
            style={{ opacity: selectedReportId && parsedUpdates && parsedUpdates.length > 0 ? 1 : 0.5 }}
          >
            Apply Updates to Platform
          </button>
        </div>
      </div>
    </div>
  );
}
